import moment from 'moment';
import { PaymentTransactionType, VendorType } from 'core/models/account/make-payment-payload.dto';
import { AccountMahService } from 'core/services/api/account-mah';
import { PaymentRequestRedirectResponse } from 'core/services/api/payment/payment-dpp';
import { ProfileService } from 'core/services/api/profile';
import { SubscriptionService } from 'core/services/api/subscriptions';
import { CurrencyStore } from 'core/stores/currency.store';
import { SiteConfigStore } from 'core/stores/site-config.store';
import { PaymentRequestType } from './payment-request';

export type DppPaymentRequest = {
  country: string;
  businessUnit: string;
  channel: string;
  currency: string;
  customerNumber: string;
  smartcardNumber: string;
  name: string;
  amount: string; //actually a double in the docs
  productUserKey: any;
  paymentType: number;
  backUrl: string;
  successReturnUrl: string;
  failureReturnUrl: string;
  bypassPaymentSuite: boolean;
  languageCode: string;
  firstName: string;
  accountNumber: string;
  accountBalance: string;
  dueDate: string;
  productNamesPrices: string;
  amountToPay: string;
  vendorType: number;
  isRecurring?: boolean;
  isRecurringUpdate?: boolean;
  ipDate?: string;
  invoicePeriod?: number;
  email: any;
  phoneNumber: string;
  consent: string;
  recurringPaymentToken: string;
  encryptedCvv: string;
}

export class PaymentRequestDPP implements PaymentRequestType<PaymentRequestRedirectResponse> {

  get hasSubscriptionDetails() {
    return (
      !!this.request.smartCardNumber
    );
  }

  get hasProfileDetails() {
    return (
      !!this.request.firstName &&
      !!this.request.name
    );
  }

  get hasAccountDetails() {
    return (
      !!this.request.accountNumber &&
      !!this.request.accountBalance &&
      !!this.request.amountToPay &&
      !!this.request.dueDate
    );
  }

  // TODO: Improve typing on request object
  constructor(private request: any, private currencyStore: CurrencyStore) { }

  addValues(values: { [key: string]: number | string | undefined }) {
    this.request = {
      ...this.request,
      ...values
    }
    return this;
  }

  getDependantValues() {
    const { customerNumber, countryName, smartCardNumber } = this.request;

    return Promise.all(
      [
        !this.hasSubscriptionDetails ? SubscriptionService.getSubscriptions(customerNumber, countryName) : Promise.resolve(),
        !this.hasProfileDetails ? ProfileService.getProfile(customerNumber, countryName) : Promise.resolve(),
        !this.hasAccountDetails ? AccountMahService.getAmountToPay(customerNumber, countryName) : Promise.resolve(),
      ]
    )
      .then(
        ([subscriptions, profile, accounts]) => {

          if (subscriptions?.subscriptions) {
            const subscription = smartCardNumber
              ? {
                smartcardNumber: smartCardNumber,
                services: []
              }
              : subscriptions.subscriptions.find(sub => sub.isPrimarySc);

            const service = subscription?.services?.find(ser => ser.type === 'Principal');

            this.addValues({
              smartcardNumber: `${subscription?.smartcardNumber}`,
              productNamesPrices: `${service?.serviceName}:${service?.price}`,
            })
          }

          if (profile?.surname) {
            this.addValues({
              firstName: profile?.firstName,
              name: profile?.surname,
            })
          }

          const account = SiteConfigStore.getAccount(accounts?.accounts);

          if (account) {
            this.addValues({
              accountNumber: account.accountNumber,
              accountBalance: `${account.accountBalance?.value}`,
              amountToPay: `${account.amountToPay?.value}`,
              dueDate: account.dueDate
            })
          }

          return this;
        }
      );
  }

  toPaymentRequestObject() {
    const { location, currencyCode, countryName, paymentType, lang, dueDate, accountBalance, amountToPay, amount, ...req } = this.request;
    const today = moment();

    const isRecurringPayment = req?.isRecurring ?? false;

    const request: DppPaymentRequest = {
      amount: `${amount}`,
      accountNumber: req.accountNumber,
      accountBalance: `${accountBalance}`,
      amountToPay: `${amountToPay}`,
      currency: this.currencyStore.mainCurrency.code,
      country: `${countryName}`.toLowerCase(),
      businessUnit: SiteConfigStore.businessUnit(),
      channel: SiteConfigStore.channel(),
      productUserKey: '',
      languageCode: lang,
      paymentType: paymentType === undefined ? PaymentTransactionType.DstvPayment : paymentType,
      bypassPaymentSuite: true,
      vendorType: this.getVendorType(isRecurringPayment), // NOTE: usd has to explicitly be handled by the DSO vendor.
      dueDate: dueDate && moment(dueDate).isSameOrAfter(today) ? dueDate : today.toJSON(),
      backUrl: req.backUrl,
      //countryCode: req.countryCode,
      customerNumber: req.customerNumber,
      failureReturnUrl: req.failureReturnUrl,
      successReturnUrl: req.successReturnUrl,
      firstName: req.firstName,
      name: req.name,
      productNamesPrices: req.productNamesPrices,
      smartcardNumber: req.smartCardNumber || req.smartcardNumber,

      isRecurring: req?.isRecurring ?? false,
      isRecurringUpdate: req?.isRecurringUpdate ?? false,
      ipDate: req?.ipDate ?? '',
      invoicePeriod: req?.invoicePeriod ?? 1,
      email: req?.email,
      phoneNumber: req?.phoneNumber,
      consent: req?.consent,
      recurringPaymentToken: req?.recurringPaymentToken,
      encryptedCvv: req.encryptedCvv,
    };

    if (request.vendorType === VendorType.None && !!req.vendorType) {
      request.vendorType = req.vendorType;
    }

    if (request.isRecurring) {
      request.amountToPay = request.amount;
    }

    return request;
  }

  getVendorType(isRecurringPayment = false) {
    if (isRecurringPayment && this.currencyStore.mainCurrency.code.toUpperCase() === 'USD') {
      return VendorType.DSO;
    }

    if (this.currencyStore.currency.code.toUpperCase() === 'USD') {
      return VendorType.DSO;
    }

    if (!!this.currencyStore.alternateCurrency?.code) {
      return VendorType.DPP;
    }

    return VendorType.None;
  }

  toTokenRequestObject() {
    return this.toPaymentRequestObject()
  }

  handleResponse(response: PaymentRequestRedirectResponse) {
    window.location.replace(response.redirectUrl);
    // window.location.assign(response.redirectUrl);
    return Promise.resolve(false)
  }
}